import {IDataClientService} from '../platform/services/data/DataClientService';
import {IAuthService} from '../platform/services/auth/AuthService';
import {ClientActiveRecord} from './dataSources/ClientActiveRecord';
import {fetchClientUsage} from '../actions/client/getClientUsage';
import {ClientMeta} from '../shared/types/SubscriptionTypes';
import {onClientUsageUpdatedSubscription} from '../graphql/clientUsage';
import {IGraphQLSubscriptionClient} from './gqlModel/IGraphQLSubscriptionClient';

export class ClientUsageTracker {
    private readonly dataClientService: IDataClientService;
    private readonly authService: IAuthService;
    private readonly clientId: string;
    private readonly clientRecord: ClientActiveRecord;
    private wsClient?: IGraphQLSubscriptionClient;
    private subscriptionKey: string;

    constructor(
        dataClientService: IDataClientService,
        authService: IAuthService,
        clientRecord: ClientActiveRecord,
        clientId: string
    ) {
        this.dataClientService = dataClientService;
        this.authService = authService;
        this.clientRecord = clientRecord;
        this.clientId = clientId;
        this.subscriptionKey = `client-usage-${clientId}`;
    }

    async init() {
        await this.refresh();
        await this.subscribe();
    }

    async refresh() {
        try {
            const usage = await fetchClientUsage(
                this.dataClientService,
                this.authService,
                this.clientId
            );
            this.applyUsage(usage);
        } catch (error) {
            console.error('Failed to fetch client usage', error);
        }
    }

    async subscribe() {
        this.wsClient = await this.dataClientService.getWsClientByKey('pgServer');
        if (!this.wsClient) {
            return;
        }

        this.wsClient.registerSubscription(
            this.subscriptionKey,
            onClientUsageUpdatedSubscription,
            this.onUsageUpdate,
            {clientId: this.clientId}
        );
    }

    destroy() {
        if (this.wsClient) {
            this.wsClient.cancelSubscription(this.subscriptionKey);
        }
    }

    private onUsageUpdate = (payload: {clientId: string} & ClientMeta) => {
        this.applyUsage(payload);
    };

    private applyUsage(usage: {clientId: string} & ClientMeta) {
        const normalized: ClientMeta = {
            itemsCount: usage.itemsCount ?? 0,
            quizzesCount: usage.quizzesCount ?? 0,
            aiUsageCount: usage.aiUsageCount ?? 0,
        };
        this.clientRecord.updateMeta(normalized);
    }
}
